import { useEffect, type ReactNode } from 'react';
import { Spinner } from '../components/Status';
import { useI18n } from '../i18n';
import { TreeScreen } from '../features/tree/TreeScreen';
import { Lazily, lazyScreen, warmScreens } from './lazyScreen';

/*
 * The tree is what nearly everybody opens the app to, so it comes with the app.
 * Everything else is fetched when it is first wanted — and, quietly, as soon as
 * the app is idle, so it is there before anybody asks (see lazyScreen.tsx). The
 * globe brings its map data with it; Admin controls is a sixth of the whole app
 * and most of the family will never see it.
 */
const WorldScreen = lazyScreen(() => import('../features/world/WorldScreen'));
const CalendarScreen = lazyScreen(() => import('../features/calendar/CalendarScreen'));
const TimelineScreen = lazyScreen(() => import('../features/timeline/TimelineScreen').then((m) => ({ default: m.TimelineScreen })));
const PassedDownScreen = lazyScreen(() => import('../features/passedDown/PassedDownScreen').then((m) => ({ default: m.PassedDownScreen })));
const ProfileScreen = lazyScreen(() => import('../features/people/ProfileScreen').then((m) => ({ default: m.ProfileScreen })));
const EditPersonScreen = lazyScreen(() => import('../features/people/EditPersonScreen').then((m) => ({ default: m.EditPersonScreen })));
const AddRelativeScreen = lazyScreen(() => import('../features/people/AddRelativeScreen').then((m) => ({ default: m.AddRelativeScreen })));
const RecordScreen = lazyScreen(() => import('../features/stories/RecordScreen').then((m) => ({ default: m.RecordScreen })));
const ManageScreen = lazyScreen(() => import('../features/admin/ManageScreen').then((m) => ({ default: m.ManageScreen })));
const WelcomeScreen = lazyScreen(() => import('../features/auth/WelcomeScreen').then((m) => ({ default: m.WelcomeScreen })));
const OnboardingScreen = lazyScreen(() => import('../features/auth/OnboardingScreen').then((m) => ({ default: m.OnboardingScreen })));
const SetPinScreen = lazyScreen(() => import('../features/auth/PinScreens').then((m) => ({ default: m.SetPinScreen })));
const UnlockScreen = lazyScreen(() => import('../features/auth/PinScreens').then((m) => ({ default: m.UnlockScreen })));

// Nobody signed in on this device: the front door is what they are about to see, so it is sent for at once rather
// than after the app has started and found that out.
try {
  if (!localStorage.getItem('edry.session') && !sessionStorage.getItem('edry.session.tab')) WelcomeScreen.preload();
} catch {
  /* storage blocked — it is fetched when it is drawn instead */
}
import { useApp, type PanelKind } from './AppState';
import { Shell } from './Shell';
import { matchPath, useRouter } from './router';
import { isFeatureOn, isPathAllowed } from './experience';

export function App() {
  const { t } = useI18n();
  const { session, loading, experience } = useApp();
  const { path, navigate } = useRouter();

  // Simple mode: screens that belong to Advanced (the globe, moments, the stories list) are not
  // reachable at all — an old link or bookmark lands on the tree instead. What each mode includes
  // is decided in app/experience.ts.
  const pathAllowed = isPathAllowed(experience, path);
  useEffect(() => {
    if (!pathAllowed && session?.onboardingComplete) navigate('/tree', { replace: true });
  }, [pathAllowed, session, navigate]);

  // Your profile, notifications and settings are panels over the current screen, not screens of
  // their own. Old links to them (a notification's "continue", a bookmark) land on the tree with
  // the panel open.
  useEffect(() => {
    const panelFor: Record<string, PanelKind> = { '/me': 'profile', '/settings': 'settings', '/notifications': 'notifications' };
    const panel = panelFor[path];
    if (!panel || !session?.onboardingComplete) return;
    // The shell opens the panel once the route has changed (see Shell).
    navigate('/tree', { replace: true, state: { openPanel: panel } });
  }, [path, session, navigate]);

  /*
   * The Moments tab is gone (a wall of every picture in upload order answered no
   * question anybody actually had). Everything that used to link there — a
   * notification about a picture added to a wedding, an old bookmark — lands on
   * the family's line instead, carrying its query along: `?memory=`, `?milestone=`
   * and `?open=` all still mean what they meant, and the Timeline acts on them.
   */
  useEffect(() => {
    if (path !== '/photos' || !session?.onboardingComplete) return;
    const home = isFeatureOn(experience, 'tab.timeline') ? '/timeline' : '/tree';
    navigate(`${home}${window.location.search}`, { replace: true });
  }, [path, session, experience, navigate]);

  // A story is a panel too. A link to one — a notification, the record screen after publishing, a
  // bookmark — lands on the family's line (or the tree, in Simple mode) with the story open on top.
  useEffect(() => {
    const story = matchPath('/stories/:id', path);
    if (!story || !session?.onboardingComplete) return;
    const home = isFeatureOn(experience, 'tab.timeline') ? '/timeline' : '/tree';
    navigate(home, { replace: true, state: { openStory: story.id } });
  }, [path, session, experience, navigate]);

  // A recipe has no screen of its own either — it lives on the circle of whoever cooks it. A link to one
  // (the notification that it was shared) lands on the tree with the recipe open on top.
  useEffect(() => {
    const recipe = matchPath('/recipes/:id', path);
    if (!recipe || !session?.onboardingComplete) return;
    navigate('/tree', { replace: true, state: { openRecipe: recipe.id } });
  }, [path, session, navigate]);

  // Send people to the right entry point.
  useEffect(() => {
    if (loading) return;
    const isAuthPath = path === '/' || path.startsWith('/welcome');
    if (!session && !isAuthPath) navigate('/', { replace: true });
    else if (session && !session.onboardingComplete && path !== '/welcome/onboarding') navigate('/welcome/onboarding', { replace: true });
    else if (session && session.onboardingComplete && isAuthPath && path !== '/welcome/pin') navigate('/tree', { replace: true });
  }, [session, loading, path, navigate]);

  // Once something is on the screen, the rest of the app is sent for in the background.
  useEffect(() => {
    if (!loading) warmScreens();
  }, [loading]);

  if (loading) return <Spinner label={t('common.loading')} />;

  // ---- unauthenticated / setup routes (no shell) ----
  if (!session) return <Lazily><WelcomeScreen /></Lazily>;
  if (session.pinLocked) return <Lazily><UnlockScreen /></Lazily>;
  if (path === '/welcome/onboarding' || !session.onboardingComplete) return <Lazily><OnboardingScreen /></Lazily>;
  if (path === '/welcome/pin') return <Lazily><SetPinScreen /></Lazily>;

  // ---- authenticated routes ----
  const personEdit = matchPath('/people/:id/edit', path);
  const personAdd = matchPath('/people/:id/add', path);
  /*
   * `/people/:id/sentence` was the relationship sentence builder ("my
   * grandfather → his father → is …"). It is gone — Jay could not work out how
   * to use it, and the one page at /add now covers everything it did by letting
   * you change who the new person is related to. An old link lands on that page
   * rather than on Not found.
   */
  const personSentence = matchPath('/people/:id/sentence', path);
  const person = matchPath('/people/:id', path);

  let screen: ReactNode;
  let fullBleed = false;

  if (!pathAllowed) {
    // Redirecting (see above); show the tree meanwhile rather than flashing the hidden screen.
    screen = <TreeScreen />;
    fullBleed = true;
  } else if (path === '/tree' || path === '/' || path === '/me' || path === '/settings' || path === '/notifications' || matchPath('/recipes/:id', path)) {
    screen = <TreeScreen />;
    fullBleed = true;
  } else if (path === '/world') {
    screen = <Lazily label={t('world.loading')}><WorldScreen /></Lazily>;
    fullBleed = true;
  } else if (path === '/calendar') {
    screen = <Lazily label={t('calendar.loading')}><CalendarScreen /></Lazily>;
    fullBleed = true;
  } else if (personEdit) screen = <Lazily><EditPersonScreen personId={personEdit.id} /></Lazily>;
  else if (personAdd) screen = <Lazily><AddRelativeScreen anchorId={personAdd.id} /></Lazily>;
  else if (personSentence) screen = <Lazily><AddRelativeScreen anchorId={personSentence.id} /></Lazily>;
  else if (person) screen = <Lazily><ProfileScreen personId={person.id} /></Lazily>;
  else if (path === '/timeline' || path === '/stories' || path === '/photos') screen = <Lazily><TimelineScreen /></Lazily>;
  else if (path === '/passed-down') screen = <Lazily><PassedDownScreen /></Lazily>;
  else if (path === '/record') screen = <Lazily><RecordScreen /></Lazily>;
  else if (path.startsWith('/manage')) screen = <Lazily><ManageScreen /></Lazily>;
  else screen = <NotFound />;

  return <Shell fullBleed={fullBleed}>{screen}</Shell>;
}

function NotFound() {
  const { t } = useI18n();
  const { navigate } = useRouter();
  return (
    <div className="page page-narrow">
      <div className="empty">
        <p>{t('error.notFound')}</p>
        <button type="button" className="btn btn-primary mt-3" onClick={() => navigate('/tree')}>
          {t('nav.tree')}
        </button>
      </div>
    </div>
  );
}
